import { useCallback, useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { useJwtToken } from "@me8eon/react_authentication";
import { EonUiButton } from "@eon-ui/eon-ui-components-react";
import type {
    HitlQueueCard,
    HitlQueueResponse,
    IsoQueueResponse,
    ItemDetail,
    Persona,
    PipelineCounts,
} from "../aidwops.types";
import useAidwopsStore from "../AidwopsStore";
import { api } from "../expertApi";
import { ExpertQueueCard } from "./ExpertQueueCard";
import { ExpertQueueFilters, type QueueRole } from "./ExpertQueueFilters";
import { RaiseRequestForm, type RequestKind } from "./RaiseRequestForm";
import "./ExpertQueue.scss";

/** The stages above the list, in the order a request travels through them. */
const PIPELINE_STAGES: Array<{
    key: keyof PipelineCounts;
    label: string;
    /** A stage that needs a person lights up while it holds something. */
    needsPerson?: boolean;
}> = [
    { key: "needsEngineer", label: "Needs you", needsPerson: true },
    { key: "awaitingIso", label: "With the ISO" },
    { key: "approved", label: "Approved" },
    { key: "applying", label: "Applying" },
    { key: "toClose", label: "To close", needsPerson: true },
    { key: "completed", label: "Completed" },
    { key: "rejected", label: "Rejected" },
];

interface ExpertQueueViewProps {
    queue?: HitlQueueResponse;
    onOpenItem?: (item: HitlQueueCard) => void;
    /** ID of the item currently open in the review pane, for highlighting its card. The Engineer body falls back to the store's `selectedItem` when this is left out. */
    selectedItemId?: string | null;
    showCustomFilter?: boolean;
    /** Whose queue this is. */
    persona?: Persona;
    /** Which status the segmented "Status" filter above the queue is set to. */
    status?: "pending" | "decided" | "all";
    /** The Role/Persona/Status filter bar is shown (showCustomFilter) when all three are supplied. */
    role?: QueueRole;
    onRoleChange?: (role: QueueRole) => void;
    onPersonaChange?: (persona: Persona) => void;
    onStatusChange?: (status: "pending" | "decided" | "all") => void;
    /** Raises a whitelist or SSL-bypass request and reloads the queue. Leave out where requests do not apply (the Intune queue). */
    onRaise?: (
        url: string,
        justification: string,
        kind: RequestKind
    ) => Promise<ItemDetail>;
    /** What approving does in this environment (staging or apply), so nobody has to guess. */
    modeNote?: string;
}

function CheckCircleIcon() {
    return (
        <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
        >
            <path
                d="M4 12.5l5 5L20 6"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
}

function QueueViewHeader({
    title,
    subtitle,
    details,
    actions,
}: {
    title: string;
    subtitle: string;
    details?: ReactNode;
    actions?: ReactNode;
}) {
    return (
        <div className="expert-queue-view-head">
            <div className="expert-queue-view-head-left">
                <span className="expert-queue-view-head-icon">
                    <CheckCircleIcon />
                </span>
                <div className="expert-queue-view-head-text">
                    <div className="expert-queue-view-title">{title}</div>
                    <div className="expert-queue-view-subtitle">{subtitle}</div>
                    {details}
                </div>
            </div>
            <span className="expert-queue-view-head-right">
                <span>
                    Ranked by impact, confidence, reversibility, and blast
                    radius
                </span>
                {actions && (
                    <span className="expert-queue-view-head-actions">
                        {actions}
                    </span>
                )}
            </span>
        </div>
    );
}

/**
 * Single entry point for both expert-queue roles: dispatches to the Engineer body (the HITL queue) or
 * the ISO body (the ISO queue), both of which are pure list renderers embedded in the same
 * AidwopsAiAssistant chat shell, which owns item selection and the review pane for either role. Keeping
 * both in one component (instead of two parallel ones) means the Role/Persona/Status filter bar and
 * queue head/pipeline markup are defined once, so the two roles can no longer drift out of sync.
 */
export default function ExpertQueueView(props: ExpertQueueViewProps) {
    if (props.role === "iso") {
        return <IsoQueueBody {...props} />;
    }
    return <EngineerQueueBody {...props} />;
}

function EngineerQueueBody({
    queue: queueOverride,
    onOpenItem,
    showCustomFilter = false,
    persona = "all",
    status = "pending",
    role,
    onRoleChange,
    onPersonaChange,
    onStatusChange,
    onRaise,
    modeNote,
}: ExpertQueueViewProps) {
    const jwtToken = useJwtToken();
    const storedQueue = useAidwopsStore((state) => state.queue);
    const storedQueueScope = useAidwopsStore((state) => state.queueScope);
    const isQueueLoading = useAidwopsStore((state) => state.isQueueLoading);
    const setSelectedItem = useAidwopsStore((state) => state.setSelectedItem);
    const selectedItem = useAidwopsStore((state) => state.selectedItem);
    const viewCustomFilter = useAidwopsStore((state) => state.viewCustomFilter);
    const setViewCustomFilter = useAidwopsStore(
        (state) => state.setViewCustomFilter
    );
    const showCompleted = status !== "pending";
    const [decidedQueue, setDecidedQueue] = useState<HitlQueueResponse | null>(
        null
    );
    const [isLoadingDecided, setIsLoadingDecided] = useState<boolean>(false);
    const [decidedError, setDecidedError] = useState<string | null>(null);
    const [isRaising, setIsRaising] = useState<boolean>(false);

    const getAccessToken = useCallback(async () => {
        const accessToken = await jwtToken();
        if (!accessToken) {
            throw new Error("Unable to acquire access token for expert API");
        }
        return accessToken;
    }, [jwtToken]);

    useEffect(() => {
        if (!showCompleted) {
            return;
        }

        let isMounted = true;
        setIsLoadingDecided(true);
        setDecidedError(null);

        void (async () => {
            try {
                const accessToken = await getAccessToken();
                const data = await api.getQueue(
                    persona,
                    accessToken,
                    status === "decided" ? "decided" : "all"
                );
                if (isMounted) {
                    setDecidedQueue(data);
                }
            } catch (error) {
                if (isMounted) {
                    setDecidedQueue(null);
                    setDecidedError(
                        error instanceof Error ? error.message : String(error)
                    );
                }
            } finally {
                if (isMounted) {
                    setIsLoadingDecided(false);
                }
            }
        })();

        return () => {
            isMounted = false;
        };
    }, [getAccessToken, persona, showCompleted, status]);

    // The "live" instance (showCustomFilter, the first chat message) must always reflect the persona
    // currently selected in the filter bar. The chat message's queue snapshot (queueOverride) is only
    // rebuilt once the chat resets for the new persona, so while that is in flight it can still hold the
    // previous persona's items: the live instance reads the store directly instead, once it has caught up
    // to the selected persona. Older, historical messages (showCustomFilter === false) keep showing their
    // own frozen snapshot, as before.
    const isLiveQueueStale = showCustomFilter && storedQueueScope !== persona;
    const activeQueue = showCompleted
        ? decidedQueue
        : showCustomFilter
          ? isLiveQueueStale
              ? null
              : storedQueue
          : (queueOverride ?? storedQueue);
    const items = activeQueue?.items ?? [];
    const queueBanner = showCompleted
        ? `${activeQueue?.items.length ?? 0} ${status === "decided" ? "decided by you" : "items"}`
        : (activeQueue?.summary?.banner ??
          `${activeQueue?.summary?.waiting ?? 0} waiting for your decision`);
    // The backend says what the person should know, for example that the queue has not been refreshed from Zscaler yet.
    const notes = showCompleted ? [] : (activeQueue?.summary?.notes ?? []);
    const openItem =
        onOpenItem ?? ((item: HitlQueueCard) => setSelectedItem(item.id));

    return (
        <section className="expert-queue-view">
            <QueueViewHeader
                title={
                    status === "decided"
                        ? "Your decisions"
                        : status === "all"
                          ? "Expert queue · all items"
                          : "Expert queue · ranked by priority"
                }
                subtitle={queueBanner}
                details={
                    <>
                        {notes.map((note) => (
                            <div key={note} className="expert-queue-view-note">
                                {note}
                            </div>
                        ))}
                        {modeNote && !showCompleted && (
                            <div className="expert-queue-view-note">
                                {modeNote}
                            </div>
                        )}
                    </>
                }
                actions={
                    showCustomFilter ? (
                        <>
                            {viewCustomFilter && !showCompleted && onRaise && (
                                <EonUiButton
                                    text="Raise request"
                                    rank="secondary"
                                    size="small"
                                    onClick={() =>
                                        setIsRaising((open) => !open)
                                    }
                                />
                            )}
                            <EonUiButton
                                icon="filter"
                                size="small"
                                rank={
                                    viewCustomFilter ? "primary" : "secondary"
                                }
                                onClick={() => {
                                    setViewCustomFilter(!viewCustomFilter);
                                }}
                            />
                        </>
                    ) : undefined
                }
            />
            {viewCustomFilter &&
                showCustomFilter &&
                role &&
                onRoleChange &&
                onPersonaChange &&
                onStatusChange && (
                    <ExpertQueueFilters
                        role={role}
                        onRoleChange={onRoleChange}
                        persona={persona}
                        onPersonaChange={onPersonaChange}
                        status={status}
                        onStatusChange={onStatusChange}
                    />
                )}

            {viewCustomFilter &&
                !showCompleted &&
                activeQueue?.summary?.pipeline && (
                    <div
                        className="queue-pipeline"
                        aria-label="Where the requests are"
                    >
                        {PIPELINE_STAGES.map((stage) => {
                            const count =
                                activeQueue.summary.pipeline?.[stage.key] ?? 0;
                            return (
                                <span
                                    key={stage.key}
                                    className={`queue-pipeline-stage${stage.needsPerson && count > 0 ? " queue-pipeline-stage--active" : ""}`}
                                >
                                    <strong>{count}</strong> {stage.label}
                                </span>
                            );
                        })}
                    </div>
                )}

            {isRaising && onRaise && (
                <RaiseRequestForm
                    onSubmit={onRaise}
                    onCancel={() => setIsRaising(false)}
                    onCreated={(created) => {
                        setIsRaising(false);
                        setSelectedItem(created.id);
                    }}
                    onOpenExisting={(id) => {
                        setIsRaising(false);
                        setSelectedItem(id);
                    }}
                />
            )}

            <div className="expert-queue-view-grid">
                {isLoadingDecided ? (
                    <p className="expert-queue-view-empty expert-queue-view-empty--loading">
                        Loading your decisions...
                    </p>
                ) : isLiveQueueStale ||
                  (showCustomFilter && !showCompleted && isQueueLoading) ? (
                    <p className="expert-queue-view-empty expert-queue-view-empty--loading">
                        Loading the queue...
                    </p>
                ) : decidedError && showCompleted ? (
                    <p className="expert-queue-view-empty" role="alert">
                        Couldn't load your decisions: {decidedError}
                    </p>
                ) : items.length > 0 ? (
                    items
                        .sort((a, b) => {
                            if (status !== "decided") return 0;

                            return (
                                new Date(b.lifecycle?.since ?? 0).getTime() -
                                new Date(a.lifecycle?.since ?? 0).getTime()
                            );
                        })
                        .map((item, index) => (
                            <ExpertQueueCard
                                key={item.id}
                                item={item}
                                rank={
                                    status === "decided"
                                        ? 0
                                        : (item.rank ?? index + 1)
                                }
                                onOpen={openItem}
                                isSelected={item.id === selectedItem}
                                showAiRecommendation={status !== "decided"}
                                showStatusInFooter={status === "decided"}
                            />
                        ))
                ) : (
                    <p className="expert-queue-view-empty">
                        {showCompleted
                            ? "You have not decided any items yet."
                            : "No queue items are available."}
                    </p>
                )}
            </div>
        </section>
    );
}

/**
 * The ISO's side of the same pipeline: the requests that reached them. A pure list renderer, like the
 * Engineer body: selection, the review pane, and every action/decision live in AidwopsAiAssistant, which
 * embeds this body in the chat shell the same way it embeds the Engineer body.
 */
function IsoQueueBody({
    status = "pending",
    role,
    onRoleChange,
    persona = "all",
    onPersonaChange,
    onStatusChange,
    onOpenItem,
    selectedItemId = null,
    showCustomFilter = true,
}: ExpertQueueViewProps) {
    const jwtToken = useJwtToken();
    const group = status;
    const viewCustomFilter = useAidwopsStore((state) => state.viewCustomFilter);
    const setViewCustomFilter = useAidwopsStore(
        (state) => state.setViewCustomFilter
    );
    const [data, setData] = useState<IsoQueueResponse | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    // Answers that arrive after the person moved on (another group) are dropped.
    const queueRequest = useRef(0);

    const getAccessToken = useCallback(async () => {
        const accessToken = await jwtToken();
        if (!accessToken) {
            throw new Error("Unable to acquire access token for expert API");
        }
        return accessToken;
    }, [jwtToken]);

    const loadQueue = useCallback(
        async (quiet = false) => {
            const request = ++queueRequest.current;
            if (!quiet) {
                setIsLoading(true);
            }
            setError(null);
            try {
                const response = await api.getIsoQueue(
                    group,
                    await getAccessToken()
                );
                if (request === queueRequest.current) {
                    setData(response);
                }
            } catch (caught) {
                if (request === queueRequest.current) {
                    setData(null);
                    setError(
                        caught instanceof Error
                            ? caught.message
                            : String(caught)
                    );
                }
            } finally {
                if (request === queueRequest.current) {
                    setIsLoading(false);
                }
            }
        },
        // getAccessToken follows useJwtToken's unstable function identity; the group is what changes what is loaded.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [group]
    );

    useEffect(() => {
        void loadQueue();
    }, [loadQueue]);

    const items = data?.items ?? [];
    const openItem = onOpenItem ?? (() => {});

    return (
        <section className="expert-queue-view">
            <QueueViewHeader
                title="ISO queue · requests for your approval"
                subtitle={data?.summary.banner ?? "Loading the ISO queue..."}
                actions={
                    <>
                        <EonUiButton
                            icon="filter"
                            size="small"
                            rank={viewCustomFilter ? "primary" : "secondary"}
                            onClick={() =>
                                setViewCustomFilter(!viewCustomFilter)
                            }
                        />
                    </>
                }
            />

            {viewCustomFilter &&
                role &&
                onRoleChange &&
                onPersonaChange &&
                onStatusChange && (
                    <ExpertQueueFilters
                        role={role}
                        onRoleChange={onRoleChange}
                        persona={persona}
                        onPersonaChange={onPersonaChange}
                        status={status}
                        onStatusChange={onStatusChange}
                    />
                )}

            {viewCustomFilter && data && (
                <div
                    className="queue-pipeline"
                    aria-label="Where the requests are"
                >
                    <span
                        className={`queue-pipeline-stage${data.summary.pending > 0 ? " queue-pipeline-stage--active" : ""}`}
                    >
                        <strong>{data.summary.pending}</strong> Pending
                    </span>
                    <span className="queue-pipeline-stage">
                        <strong>{data.summary.approved}</strong> Approved
                    </span>
                    <span className="queue-pipeline-stage">
                        <strong>{data.summary.toClose}</strong> To close
                    </span>
                    <span className="queue-pipeline-stage">
                        <strong>{data.summary.completed}</strong> Completed
                    </span>
                    <span className="queue-pipeline-stage">
                        <strong>{data.summary.rejected}</strong> Rejected
                    </span>
                    <span className="queue-pipeline-stage">
                        <strong>{data.summary.failed}</strong> Failed
                    </span>
                </div>
            )}

            <div className="expert-queue-view-grid">
                {isLoading && !data ? (
                    <p className="expert-queue-view-empty expert-queue-view-empty--loading">
                        Loading the ISO queue...
                    </p>
                ) : error ? (
                    <p className="expert-queue-view-empty" role="alert">
                        Couldn't load the ISO queue: {error}{" "}
                        <button
                            type="button"
                            className="triage-link"
                            onClick={() => void loadQueue()}
                        >
                            Try again
                        </button>
                    </p>
                ) : items.length === 0 ? (
                    <p className="expert-queue-view-empty">
                        {group === "pending"
                            ? "Nothing is waiting for your approval."
                            : "No requests here yet."}
                    </p>
                ) : (
                    items
                        .sort((a, b) => {
                            if (status !== "decided") return 0;

                            return (
                                new Date(b.lifecycle?.since ?? 0).getTime() -
                                new Date(a.lifecycle?.since ?? 0).getTime()
                            );
                        })
                        .map((item, index) => (
                            <div key={item.id}>
                                <ExpertQueueCard
                                    item={item}
                                    rank={
                                        status === "decided"
                                            ? 0
                                            : (item.rank ?? index + 1)
                                    }
                                    onOpen={() => openItem(item)}
                                    isSelected={item.id === selectedItemId}
                                    showAiRecommendation={status !== "decided"}
                                    showStatusInFooter={status === "decided"}
                                />
                                <div className="expert-queue-view-note">
                                    Requested by {item.requestedBy || "unknown"}
                                    {item.url ? ` · ${item.url}` : ""}
                                    {item.engineerComment
                                        ? ` · ${item.engineerBy ?? "Engineer"}: ${item.engineerComment}`
                                        : ""}
                                </div>
                            </div>
                        ))
                )}
            </div>
        </section>
    );
}
